// Guided conversations (28 Oct 2026): the rules the pages share, with no
// React and no network. A Manager writes a guide (goal plus agenda points) and
// invites people; each person works through it with a `guided` agent chat.
// Design: design/2026-10-28-guided-conversations.md.

import type {
  GuideMineItem,
  GuideNote,
  GuideNoteCuration,
  GuideNoteType,
  GuidePoint,
  GuidePointState,
  GuidePointStatus,
  GuideReader,
  GuideResponse,
  GuideResponseStatus,
  GuideResponseView,
} from '../types/gotypes'
import type { AgentMessage } from '../agent/types'
import { refuse, type WhoToAsk } from './permissionReason'

/** The agent's guide tools (the `platinum` MCP server, guided chats only). */
export const GUIDE_TOOLS = [
  'guide_status',
  'guide_set_active_point',
  'guide_mark_point',
  'guide_write_note',
  'guide_revise_note',
  'guide_finish',
] as const

/** A guide tool, by its bare name or as the CLI names it (`mcp__platinum__guide_mark_point`). */
export function isGuideTool(name: string): boolean {
  return GUIDE_TOOLS.some((t) => name === t || name.endsWith(`__${t}`))
}

/**
 * How many guide tool calls in the chat have ended (done, failed or
 * cancelled). The page refetches the response when this number grows, so the
 * tracker ticks a point the moment the agent's call ends, with no reload.
 */
export function settledGuideToolCount(messages: AgentMessage[]): number {
  let n = 0
  for (const m of messages) {
    for (const tc of m.toolCalls ?? []) {
      if (tc.status !== 'running' && isGuideTool(tc.name)) n++
    }
  }
  return n
}

export const POINT_STATUS_LABELS: Record<GuidePointStatus, string> = {
  pending: 'Not started',
  active: 'Talking about it now',
  covered: 'Covered',
  skipped: 'Skipped for now',
  declined: 'Left out',
}

export const RESPONSE_STATUS_LABELS: Record<GuideResponseStatus, string> = {
  invited: 'Invited',
  in_progress: 'In progress',
  finished: 'Finished',
  finished_early: 'Finished early',
  withdrawn: 'Withdrawn',
}

export const NOTE_TYPE_LABELS: Record<GuideNoteType, string> = {
  fact: 'Fact',
  definition: 'Definition',
  metric: 'Metric',
  procedure: 'Procedure',
  decision_rule: 'Decision rule',
  pain_point: 'Pain point',
  source: 'Source',
  tool_request: 'Tool request',
  example: 'Example',
}
export const NOTE_TYPES = Object.keys(NOTE_TYPE_LABELS) as GuideNoteType[]

export const CURATION_LABELS: Record<GuideNoteCuration, string> = {
  none: 'Not tagged',
  context: 'Context',
  skill: 'Skill',
  tool_request: 'Tool request',
  discard: 'Discard',
}
export const CURATIONS = Object.keys(CURATION_LABELS) as GuideNoteCuration[]

/** A point's state; a point with no row yet is pending. */
export function pointStatus(states: GuidePointState[], pointId: string): GuidePointStatus {
  return states.find((s) => s.point_id === pointId)?.status ?? 'pending'
}

/** Covered or left out: nothing more to ask. */
export function isPointDone(status: GuidePointStatus): boolean {
  return status === 'covered' || status === 'declined'
}

/** How far through the agenda: covered and left-out points are both done (the bar), but only covered ones are "covered". */
export function progressOf(points: GuidePoint[], states: GuidePointState[]): { done: number; covered: number; leftOut: number; total: number } {
  const statuses = points.map((p) => pointStatus(states, p.id))
  const covered = statuses.filter((s) => s === 'covered').length
  const leftOut = statuses.filter((s) => s === 'declined').length
  return { done: covered + leftOut, covered, leftOut, total: points.length }
}

/** "2 of 3 points covered · 1 left out" (the left-out part only when there is one). */
export function progressLabel(points: GuidePoint[], states: GuidePointState[], noun = 'points covered'): string {
  const p = progressOf(points, states)
  return `${p.covered} of ${p.total} ${noun}${p.leftOut ? ` · ${p.leftOut} left out` : ''}`
}

/** What clicking a point puts in the composer. The person sends it. */
export function coverPrompt(point: GuidePoint): string {
  return `Can we cover "${point.title}" now?`
}

/** What "Skip for now" puts in the composer: skipping needs the person's own words. */
export function skipPrompt(point: GuidePoint): string {
  return `Can we skip "${point.title}" for now and come back to it later?`
}

/**
 * The person's guided conversations that can still go on, for History's
 * resume box: invited or in progress, on an open guide. The one they worked
 * on last comes first, then invitations, newest first.
 */
export function resumableGuides(items: GuideMineItem[]): GuideMineItem[] {
  const recency = (i: GuideMineItem) => i.response.last_active_at ?? i.response.invited_at
  return items
    .filter(i => i.guide_status === 'open' && (i.response.status === 'in_progress' || i.response.status === 'invited'))
    .sort((a, b) => {
      const started = Number(b.response.status === 'in_progress') - Number(a.response.status === 'in_progress')
      return started || recency(b) - recency(a)
    })
}

/** What a person coming back finds in the reply box (Matt: the agent should ask if they want to carry on). */
export const WELCOME_BACK_MESSAGE = "I'm back. Let's carry on."

/** How long since the last message before an opened conversation counts as a return. */
export const WELCOME_BACK_AFTER_MS = 30 * 60 * 1000

/** True when the conversation's last message is older than WELCOME_BACK_AFTER_MS: the person is coming back. */
export function isComingBack(messages: Pick<AgentMessage, 'timestamp'>[], now: number): boolean {
  const last = messages[messages.length - 1]
  if (!last) return false
  const at = Date.parse(last.timestamp)
  return !Number.isNaN(at) && now - at >= WELCOME_BACK_AFTER_MS
}

/** The first thing sent for the person when they press Start, so the agent opens the conversation. */
export const OPENING_MESSAGE = 'Hello, I am ready to start.'

/** The live notes for one point (null: the notes about no point), oldest first. */
export function notesFor<N extends Pick<GuideNote, 'point_id' | 'created_at'>>(notes: N[], pointId: string | null): N[] {
  return notes
    .filter((n) => (n.point_id ?? null) === pointId)
    .sort((a, b) => a.created_at - b.created_at)
}

/** The response is over: finished, finished early or withdrawn. */
export function isResponseOver(status: GuideResponseStatus): boolean {
  return status === 'finished' || status === 'finished_early' || status === 'withdrawn'
}

/**
 * Why the person on the page may not change this conversation, or undefined
 * when they may. The server's `can_edit` is the rule; this words it.
 */
export function guidedEditReason(view: GuideResponseView, viewerEmail: string, who: WhoToAsk): string | undefined {
  if (view.can_edit) return undefined
  const owner = view.response.email
  if (owner && owner.toLowerCase() !== viewerEmail.toLowerCase()) {
    return refuse('You can’t change someone else’s guided conversation.', who, { owner }).reason
  }
  if (view.response.status === 'withdrawn') return 'This invitation was withdrawn, so the conversation is read-only.'
  if (view.guide.status === 'closed') return 'This guide is closed, so the conversation is read-only.'
  if (isResponseOver(view.response.status)) return 'You finished this conversation, so it is read-only.'
  return 'This conversation is read-only.'
}

/**
 * Why the person on the page may not change the notes, or undefined when
 * they may. Notes stay theirs to correct after the conversation finishes,
 * until the guide closes; the server's `can_edit_notes` is the rule.
 */
export function guidedNotesReason(view: GuideResponseView, viewerEmail: string, who: WhoToAsk): string | undefined {
  if (view.can_edit_notes) return undefined
  return guidedEditReason({ ...view, can_edit: false }, viewerEmail, who)
}

/** The full link to a response, from the server's path. */
export function guidedLink(path: string): string {
  if (/^https?:\/\//.test(path)) return path
  return `${window.location.origin}${path.startsWith('/') ? '' : '/'}${path}`
}

/** A new point's id: unique within the guide, stable across saves. */
export function newPointId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return `p-${c.randomUUID().slice(0, 8)}`
  return `p-${Math.random().toString(36).slice(2, 10)}`
}

/** "28 Oct 2026, 14:05", or "" for no time. */
export function formatGuideTime(ms: number | null | undefined): string {
  if (!ms) return ''
  return new Date(ms).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** The emails in a pasted list: split on spaces, commas, semicolons and lines; lower-case; each once. */
export function parseEmails(text: string): string[] {
  const seen = new Set<string>()
  for (const part of text.split(/[\s,;]+/)) {
    const email = part.trim().replace(/^<|>$/g, '').toLowerCase()
    if (email.includes('@')) seen.add(email)
  }
  return [...seen]
}

/** Why an invitation cannot be withdrawn; undefined when it can. */
export function withdrawReason(response: Pick<GuideResponse, 'status'>): string | undefined {
  if (response.status === 'withdrawn') return 'This invitation is already withdrawn.'
  if (isResponseOver(response.status)) return 'This person has finished; their answers stay.'
  return undefined
}

/**
 * Who reads a person's answers, in the one wording the intro, the agenda
 * print and the side panel use: the Managers always, then the reviewers a
 * Manager named on the guide (migration 000190), by name and email.
 */
export function readerLines(reviewers: GuideReader[]): string[] {
  const lines = ['Platinum Managers can read your notes and this conversation.']
  if (reviewers.length > 0) {
    const names = reviewers.map((r) => (r.name && r.name !== r.email ? `${r.name} (${r.email})` : r.email))
    lines.push(`${joinNames(names)} can read your progress and your notes, not the conversation.`)
  }
  return lines
}

/** The side panel's one line: "Read by Platinum Managers and Rita Shah." */
export function readByLine(reviewers: GuideReader[]): string {
  return `Read by ${joinNames(['Platinum Managers', ...reviewers.map((r) => r.name || r.email)])}.`
}

function joinNames(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
